import {
  Body,
  Box,
  Button,
  CheckButton,
  Flex,
  Grid,
  Heading,
  SimpleGrid,
  Stack,
  TextInput,
  system,
  useTheme,
} from '@hover/blueprint';
import React, { useContext, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';
import { type InferType } from 'yup';

import { OnboardingFormFooter } from 'src/modules/onboarding/components/OnboardingFormFooter';
import { StyledCheck } from 'src/modules/onboarding/components/StyledCheck';
import { makeOrgUsageValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { Form as SharedForm } from 'src/shared/components/forms';
import {
  UserPersonas,
  useUpdateUserMutation,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { pageViewed } from 'src/shared/telemetry';

import { OnboardingContext } from './OnboardingContext';

export type UserOrgFormData = InferType<
  ReturnType<typeof makeOrgUsageValidationSchema>
>;

interface OrgFormData {
  orgUsage: UserPersonas[];
  customDescription?: string;
}

interface UserOrgUsageFormFieldsProps {
  skip: () => void;
}

const UserOrgUsageFormFields = ({ skip }: UserOrgUsageFormFieldsProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { breakpoints } = useTheme();

  const constructionOptions = [
    {
      value: UserPersonas.Owner,
      text: t('onboarding.owner'),
    },
    {
      value: UserPersonas.Executive,
      text: t('onboarding.executive'),
    },
    {
      value: UserPersonas.SalesManager,
      text: t('onboarding.salesManager'),
    },
    {
      value: UserPersonas.SalesRep,
      text: t('onboarding.salesRep'),
    },
    {
      value: UserPersonas.Estimator,
      text: t('onboarding.estimator'),
    },
    {
      value: UserPersonas.ProductionManager,
      text: t('onboarding.productionManager'),
    },
    {
      value: UserPersonas.ProjectInstallationManager,
      text: t('onboarding.projectInstallationManager'),
    },
    {
      value: UserPersonas.Marketing,
      text: t('onboarding.marketing'),
    },
    {
      value: UserPersonas.Finance,
      text: t('onboarding.finance'),
    },
  ];

  const insuranceOptions = [
    {
      value: UserPersonas.Executive,
      text: t('onboarding.executive'),
    },
    {
      value: UserPersonas.OperationsManager,
      text: t('onboarding.operationsManager'),
    },
    {
      value: UserPersonas.ClaimsManager,
      text: t('onboarding.claimsManager'),
    },
    {
      value: UserPersonas.DeskAdjuster,
      text: t('onboarding.deskAdjuster'),
    },
    {
      value: UserPersonas.FieldAdjuster,
      text: t('onboarding.fieldAdjuster'),
    },
    {
      value: UserPersonas.CatastropheAdjuster,
      text: t('onboarding.catastropheAdjuster'),
    },
    {
      value: UserPersonas.ClaimsInspector,
      text: t('onboarding.claimsInspector'),
    },
    {
      value: UserPersonas.Underwriting,
      text: t('onboarding.underwriting'),
    },
    {
      value: UserPersonas.Finance,
      text: t('onboarding.finance'),
    },
  ];

  const isMobile = useIsMobileSize();

  const [contextState] = useContext(OnboardingContext);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { setValue, register, control } = useFormContext();
  const [checkedItem, setCheckedItem] = useState<string | undefined>(undefined);
  const [otherOpen, setOtherOpen] = useState(false);

  const onCheckUsage = (val: UserPersonas) => {
    setOtherOpen(false);
    if (checkedItem === val) {
      setCheckedItem(undefined);
      setValue('orgUsage', undefined);
    } else {
      setCheckedItem(val);
      setValue('orgUsage', val);
    }
  };

  const options =
    contextState.orgType === 'insurance'
      ? insuranceOptions
      : constructionOptions;

  return (
    <Stack
      direction={{ base: 'column', tablet: 'row' }}
      flex={1}
      flexWrap="nowrap"
      justifyContent={{ base: 'start', tablet: 'space-around' }}
      marginTop={300}
      maxWidth={{ base: 'auto', tablet: breakpoints.width500 }}
      spacing={0}
      width={1}
    >
      <Flex
        flexBasis={{ base: 'auto', tablet: 750 }}
        flexGrow="1"
        flexShrink="1"
        mb={{ base: 400, tablet: 0 }}
      >
        <Flex flexDir="column" maxW={750}>
          <Heading
            mb={{ base: 400, tablet: 200 }}
            size={{ base: 600, tablet: 800 }}
            testId="user-org-description-heading"
          >
            {t('onboarding.howWouldYouDescribe')}
          </Heading>
          <Body color="neutral.600" mt={0}>
            {t('onboarding.selectAnOption')}
          </Body>
        </Flex>
      </Flex>
      <Flex
        alignItems="center"
        flexBasis={750}
        flexDir="column"
        flexGrow="2"
        flexShrink="0"
      >
        <SimpleGrid
          columns={{ base: 1, tablet: 2 }}
          height="fit-content"
          spacingX={500}
          spacingY={300}
          width={1}
        >
          {options.map(option => (
            <CheckButton
              isChecked={checkedItem === option.value}
              key={option.value}
              name="orgUsage"
              onChange={() => {
                onCheckUsage(option.value);
              }}
              overflowWrap="break-word"
              ref={register}
              size="large"
              textAlign="center"
              value={option.value}
              whiteSpace="break-spaces"
            >
              <system.span alignItems="center" display="inline-flex">
                {option.text}
                <StyledCheck isChecked={checkedItem === option.value} />
              </system.span>
            </CheckButton>
          ))}
          <CheckButton
            isChecked={otherOpen}
            onChange={() => {
              setCheckedItem(undefined);
              setOtherOpen(!otherOpen);
            }}
            size="large"
          >
            <system.span alignItems="center" display="inline-flex">
              {t('onboarding.other')}
              <StyledCheck isChecked={otherOpen} />
            </system.span>
          </CheckButton>
        </SimpleGrid>
        {otherOpen ? (
          <Controller
            control={control}
            defaultValue=""
            name="customDescription"
            render={props => (
              <TextInput
                bgColor="neutral.100"
                borderColor="neutral.100"
                borderRadius="15px"
                mt={500}
                onChange={val => props.onChange(val)}
                placeholder={t('onboarding.describeYourBusinessHere')}
              />
            )}
          />
        ) : (
          !isMobile && (
            <Button mt={600} onClick={skip} variant="ghost" width="fit-content">
              {t('onboarding.skip')}
            </Button>
          )
        )}
      </Flex>
    </Stack>
  );
};

export const UserOrgUsage = () => {
  const history = useHistory();
  const { search } = useLocation();

  const [updateUser] = useUpdateUserMutation();

  const validationSchema = makeOrgUsageValidationSchema();
  const initialValues = {
    orgUsage: [UserPersonas.Other],
    customDescription: '',
  };

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-projects_input',
      feature: 'onboarding_reskin',
    });
  });

  const navigateToPropertyList = () => {
    history.push(`${Routes.PROPERTIES.path}${search}`);
  };

  const onSubmit = (formData: OrgFormData) => {
    const { orgUsage, customDescription } = formData;

    if (orgUsage[0] || customDescription) {
      void updateUser({
        variables: {
          personaDescription: customDescription,
          persona: orgUsage[0] ?? UserPersonas.Other,
        },
      });
    }

    navigateToPropertyList();
  };

  return (
    <Box
      alignItems="center"
      flexDirection="column"
      gap={300}
      height={1}
      padding={500}
      width="100%"
    >
      <Grid alignItems="stretch" flex={1} width={1}>
        <SharedForm<UserOrgFormData>
          alignItems="center"
          display="flex"
          height={1}
          initialValues={initialValues}
          onSubmit={onSubmit}
          transparentFieldset
          validationSchema={validationSchema}
          w={1}
        >
          <UserOrgUsageFormFields skip={navigateToPropertyList} />
          <OnboardingFormFooter hideForMobile={false} />
        </SharedForm>
      </Grid>
    </Box>
  );
};
